/**
 * resolveScenarioCore.ts
 * FEAT-2026-0007 Phase 6D.4 / BUG-2026-0118 — scenario resolve decision logic.
 *
 * Pure, dependency-injected core for the create-or-load-draft + generate flow.
 * Extracted from useResolveScenarioForSource.ts so the logic can be unit-tested
 * under tsx with fake services (the real service modules transitively import
 * apiClient → constants/config, whose `import.meta.env.VITE_API_URL` only
 * exists under Vite — importing them from a plain tsx test crashes at load).
 *
 * Only `import type` from the service modules is allowed here (erased at
 * compile time — no runtime dependency). Real bindings live in
 * useResolveScenarioForSource.ts.
 *
 * Resolution sequence:
 *   1. getActiveScenarioForSource(sourceType, sourceKey)
 *      - found → RESUME path:
 *          a. probe listTasksForSource(sourceType, sourceKey, ScenarioKey)
 *          b. ≥1 live task → return the draft as-is (true resume)
 *          c. 0 live tasks → re-run generateScheduleTasks under the SAME
 *             ScenarioKey, then return the draft with a "regenerated" message
 *             (BUG-2026-0118 — see below)
 *          d. probe failed → return the draft as-is (never let the probe
 *             break the resume happy path)
 *   2. not found → LOCKED-PLAN CHECK (2026-07-10 silent-duplicate fix):
 *          listScenariosForSource(sourceType, sourceKey) — when the source's
 *          only plan is Submitted (locked), return { kind: 'locked' } so the
 *          caller can offer Open / Reopen / Create-new EXPLICITLY instead of
 *          silently minting a duplicate fully-booked draft. Skipped when
 *          opts.forceCreate (the user chose "Create new draft").
 *   3. still nothing → CREATE path (unchanged):
 *          a. createScenarioDraft({ SourceType, SourceKey })
 *          b. generateScheduleTasks({ source, sourceKey, scenarioKey })
 *             (soft failure: error reported in message, scenario still returned)
 *
 * BUG-2026-0118 — "work order is not shown in the scenario schedule."
 * A resumed draft is NOT guaranteed to still hold tasks: its ScheduleTask rows
 * can be soft-deleted after the draft was created (in prod, SP-003's two tasks
 * were soft-deleted by a demo-seeding cleanup while the Draft row survived).
 * The old resume path assumed "tasks already exist" and returned the hollow
 * draft, landing the user on an empty Phase 1 with no explanation. Now an
 * empty resumed plan re-materialises its tasks from the source. Regeneration
 * is safe to re-run: the backend's natural-key lookup (findExistingTask in
 * scheduleTaskGenerationHelpers.ts) excludes soft-deleted rows, so fresh
 * ScheduleTask rows are inserted, and bookingExists prevents duplicate
 * bookings. It only fires when the plan has ZERO live tasks — a partially
 * curated plan (some tasks deliberately removed) is never touched.
 */

import type {
  Scenario,
  ScenarioSourceType,
} from '../../../../services/scheduleScenario';
import type {
  GenerateTasksRequest,
  GenerateTasksResult,
} from '../../../../services/scheduleTaskGeneration';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ResolveResult {
  kind: 'resolved';
  scenario: Scenario;
  message: string;
  severity: 'success' | 'info';
}

/**
 * 2026-07-10 silent-duplicate fix: the source has NO editable draft but DOES
 * have a locked (Submitted) plan. The resolver refuses to silently create a
 * duplicate — the caller must offer the user an explicit choice:
 * Open plan (read-only) / Reopen to Edit / Create new draft (forceCreate).
 */
export interface ResolveLocked {
  kind: 'locked';
  /** The newest locked (Submitted) plan for this source. */
  scenario: Scenario;
}

export type ResolveOutcome = ResolveResult | ResolveLocked;

/** Options for resolveScenarioWithDeps. */
export interface ResolveScenarioOpts {
  /**
   * Skip the locked-plan check and create a fresh draft (the pre-2026-07-10
   * behaviour) — set ONLY when the user explicitly chose "Create new draft"
   * in the locked-plan dialog.
   */
  forceCreate?: boolean;
}

/**
 * Injected service functions. Production callers bind the real services in
 * useResolveScenarioForSource.ts; tests inject fakes.
 */
export interface ResolveScenarioDeps {
  getActiveScenarioForSource(
    source: ScenarioSourceType,
    sourceKey: string,
  ): Promise<Scenario | null>;
  createScenarioDraft(payload: {
    SourceType: ScenarioSourceType;
    SourceKey: string;
  }): Promise<Scenario>;
  generateScheduleTasks(request: GenerateTasksRequest): Promise<GenerateTasksResult>;
  listTasksForSource(
    source: ScenarioSourceType,
    sourceKey: string,
    scenarioKey?: string,
  ): Promise<TaskWithBookings[]>;
  /** ALL scenarios (any status) for a source — the locked-plan probe. */
  listScenariosForSource(
    source: ScenarioSourceType,
    sourceKey: string,
  ): Promise<Scenario[]>;
}

/** Outcome of a generate-tasks call that must never throw to the caller. */
type GenerationOutcome =
  | { ok: true; gen: GenerateTasksResult }
  | { ok: false; errorMessage: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Runs task generation, converting any thrown error into a soft-failure
 * outcome. Shared by the create path and the BUG-2026-0118 empty-resume path.
 */
async function runTaskGeneration(
  deps: ResolveScenarioDeps,
  sourceType: ScenarioSourceType,
  sourceKey: string,
  scenarioKey: string,
): Promise<GenerationOutcome> {
  try {
    const gen = await deps.generateScheduleTasks({
      source: sourceType,
      sourceKey,
      scenarioKey,
    });
    return { ok: true, gen };
  } catch (genErr: unknown) {
    const errorMessage =
      genErr instanceof Error ? genErr.message : 'Unknown error generating tasks.';
    return { ok: false, errorMessage };
  }
}

/** Human noun for snackbar copy: 'quote' | 'work order'. */
function sourceNoun(sourceType: ScenarioSourceType): string {
  return sourceType === 'Quote' ? 'quote' : 'work order';
}

/**
 * Warning suffix for tests that couldn't be mapped to the scheduling catalog and
 * were skipped (no catalog item / no task-type mapping). Empty when none.
 * Surfacing this turns a previously-silent drop into a visible message.
 */
function unmappedSuffix(gen: GenerateTasksResult): string {
  const u = gen.unmappedTests ?? [];
  const unmapped = u.length === 0
    ? ''
    : ` ⚠ ${u.length} test(s) couldn't be added to the schedule (no catalog mapping): ${u.join(', ')}.`;
  // Migration 111: deliberate catalogue opt-outs — informational, no ⚠.
  const e = gen.excludedTests ?? [];
  const excluded = e.length === 0
    ? ''
    : ` ${e.length} test(s) excluded from scheduling by the test catalogue: ${e.join(', ')}.`;
  return `${unmapped}${excluded}`;
}

// ---------------------------------------------------------------------------
// Core resolve function
// ---------------------------------------------------------------------------

/**
 * Resolves (or creates) a scenario for the given source, using the injected
 * service dependencies. See the module docstring for the full sequence.
 *
 * Throws on hard failures (getActiveScenarioForSource non-404 error,
 * listScenariosForSource error — NEVER silently create when the locked-plan
 * probe can't answer — createScenarioDraft network error). Task generation
 * failures are soft — the scenario is still returned with an explanatory
 * message.
 */
export async function resolveScenarioWithDeps(
  sourceType: ScenarioSourceType,
  sourceKey: string,
  deps: ResolveScenarioDeps,
  opts?: ResolveScenarioOpts,
): Promise<ResolveOutcome> {
  // Step 1: check for an existing resumable draft
  const existing = await deps.getActiveScenarioForSource(sourceType, sourceKey);

  if (existing) {
    const resumePrefix = `Resuming draft ${existing.Code} — ${existing.Name}.`;

    // BUG-2026-0118: probe the live task list before trusting the resume.
    // null = probe failed (unknown) → fall back to a plain resume.
    let liveTaskCount: number | null = null;
    try {
      const tasks = await deps.listTasksForSource(
        sourceType,
        sourceKey,
        existing.ScenarioKey,
      );
      liveTaskCount = tasks.length;
    } catch {
      liveTaskCount = null;
    }

    if (liveTaskCount === 0) {
      // Hollow draft — re-materialise tasks under the SAME ScenarioKey.
      const regen = await runTaskGeneration(
        deps,
        sourceType,
        sourceKey,
        existing.ScenarioKey,
      );

      if (!regen.ok) {
        return {
          kind: 'resolved',
          scenario: existing,
          message:
            `${resumePrefix} The plan had no tasks and regeneration failed: ` +
            regen.errorMessage,
          severity: 'info',
        };
      }

      if (regen.gen.tasksCreated === 0) {
        return {
          kind: 'resolved',
          scenario: existing,
          message:
            `${resumePrefix} The plan has no tasks and none could be generated ` +
            `from the ${sourceNoun(sourceType)} — use "+ Add Task" to add them manually.`,
          severity: 'info',
        };
      }

      const unplaced = regen.gen.unplacedTaskTypes.length;
      const regenUnmapped = unmappedSuffix(regen.gen);
      if (unplaced > 0) {
        return {
          kind: 'resolved',
          scenario: existing,
          message:
            `${resumePrefix} The plan had no tasks — regenerated ` +
            `${regen.gen.tasksCreated} task(s); ${unplaced} task type(s) ` +
            `could not be placed on a machine.${regenUnmapped}`,
          severity: 'info',
        };
      }

      return {
        kind: 'resolved',
        scenario: existing,
        message:
          `${resumePrefix} The plan had no tasks — regenerated ` +
          `${regen.gen.tasksCreated} task(s) from the ${sourceNoun(sourceType)}.${regenUnmapped}`,
        severity: regenUnmapped ? 'info' : 'success',
      };
    }

    // True resume (≥1 live task, or probe failed): unchanged behaviour.
    return {
      kind: 'resolved',
      scenario: existing,
      message: `Resuming existing draft ${existing.Code} — ${existing.Name}`,
      severity: 'info',
    };
  }

  // Step 2 (2026-07-10 silent-duplicate fix): no editable draft. Before
  // auto-creating, check whether the source already has a LOCKED (Submitted)
  // plan — the old flow silently created a brand-new draft here and
  // auto-placed every booking (real case: SP-089 Submitted → visit → SP-091
  // born with 9 bookings). Archived plans do NOT block (deliberately shelved);
  // a probe failure THROWS rather than risking a duplicate.
  if (!opts?.forceCreate) {
    const allForSource = await deps.listScenariosForSource(sourceType, sourceKey);
    const locked = allForSource.find((s) => s.Status === 'Submitted');
    if (locked) {
      return { kind: 'locked', scenario: locked };
    }
  }

  // Step 3a: create a new draft scenario
  const scenario = await deps.createScenarioDraft({
    SourceType: sourceType,
    SourceKey: sourceKey,
  });

  // Step 3b: generate schedule tasks under this draft's ScenarioKey.
  // Soft failure: if generate-tasks fails (e.g. quote not yet approved), the
  // scenario was still created and the user can add tasks manually in Phase 1.
  const outcome = await runTaskGeneration(
    deps,
    sourceType,
    sourceKey,
    scenario.ScenarioKey,
  );

  if (!outcome.ok) {
    return {
      kind: 'resolved',
      scenario,
      message: `Scenario created, but task generation failed: ${outcome.errorMessage}`,
      severity: 'info',
    };
  }

  const unplaced = outcome.gen.unplacedTaskTypes.length;
  const createUnmapped = unmappedSuffix(outcome.gen);
  if (unplaced > 0) {
    return {
      kind: 'resolved',
      scenario,
      message:
        `Scenario created. ${outcome.gen.tasksCreated} task(s) generated. ` +
        `${unplaced} task type(s) could not be placed on a machine.${createUnmapped}`,
      severity: 'info',
    };
  }

  return {
    kind: 'resolved',
    scenario,
    message:
      `Scenario ${scenario.Code} created with ${outcome.gen.tasksCreated} task(s).${createUnmapped}`,
    severity: createUnmapped ? 'info' : 'success',
  };
}
